/**
 * useNotificationPermission
 * Orchestrates the notification permission ask for the validation reminder.
 *
 * Rules (iOS-driven):
 * - The OS prompt is ONE-SHOT. Once shown, a refusal is permanent — we must
 *   never trigger it cold and never re-trigger it.
 * - Our custom pre-prompt IS re-showable. We gate the OS prompt behind it.
 * - Anchor: a parent's task validation (the value was just experienced).
 *
 * Re-ask cadence (back-off) while not yet granted and OS prompt not yet shown.
 * Each entry is "minimum days since last shown" for that step:
 *   - 3× at 1-day spacing   (days ~1, 2, 3)
 *   - 3× at 3-day spacing   (days ~6, 9, 12)
 *   - 3× at 7-day spacing   (weeks ~3, 4, 5)
 *   - then every 30 days, indefinitely
 *
 * State is a module-level singleton so the app-level sheet and the validation
 * flow share it.
 */
import { ref } from 'vue'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import {
  hasNotificationPermission,
  requestNotificationPermission,
  registerCurrentToken,
} from '@/services/push-token.service'
import { feedbackService } from '@/services/feedback.service'

// Set once we've shown the OS prompt, so we never trigger it again.
const OS_PROMPTED_KEY = 'validationReminder.osPrompted'
// Number of times the pre-prompt has been shown.
const SHOWN_COUNT_KEY = 'validationReminder.prePromptShownCount'
// Epoch ms of the last pre-prompt display.
const LAST_SHOWN_KEY = 'validationReminder.prePromptLastShown'

const DAY_MS = 24 * 60 * 60 * 1000

// Minimum spacing (in days) before showing again, by prior-show count.
// Index = how many times already shown. Beyond the array → last value repeats.
const BACKOFF_DAYS = [
  0, // 1st show: immediately (no prior show)
  1, // 2nd: +1 day
  1, // 3rd: +1 day
  3, // 4th: +3 days
  3, // 5th: +3 days
  3, // 6th: +3 days
  7, // 7th: +7 days
  7, // 8th: +7 days
  7, // 9th: +7 days
  30, // 10th and beyond: +30 days
]

const showPrePrompt = ref(false)
let evaluating = false

function spacingForCount(count: number): number {
  return BACKOFF_DAYS[Math.min(count, BACKOFF_DAYS.length - 1)]
}

async function shouldShowNow(): Promise<boolean> {
  const [{ value: countRaw }, { value: lastRaw }] = await Promise.all([
    Preferences.get({ key: SHOWN_COUNT_KEY }),
    Preferences.get({ key: LAST_SHOWN_KEY }),
  ])
  const count = countRaw ? parseInt(countRaw, 10) || 0 : 0
  const last = lastRaw ? parseInt(lastRaw, 10) || 0 : 0

  const requiredDays = spacingForCount(count)
  if (count === 0) return true // never shown → show now
  const elapsedDays = (Date.now() - last) / DAY_MS
  return elapsedDays >= requiredDays
}

async function recordShown(): Promise<void> {
  const { value: countRaw } = await Preferences.get({ key: SHOWN_COUNT_KEY })
  const count = countRaw ? parseInt(countRaw, 10) || 0 : 0
  await Promise.all([
    Preferences.set({ key: SHOWN_COUNT_KEY, value: String(count + 1) }),
    Preferences.set({ key: LAST_SHOWN_KEY, value: String(Date.now()) }),
  ])
}

/**
 * Call after a parent's task validation. Decides whether to surface the custom
 * pre-prompt, honoring the back-off cadence. No-op on web, when already granted,
 * or when the OS prompt was already shown once.
 */
async function maybeAskAfterValidation(): Promise<void> {
  // Session-wide mutex: never stack our pre-prompt on top of (or right after)
  // the rating ask, and never show a 2nd system prompt in the same session.
  const { useFeedbackStore } = await import('@/stores/feedback')
  if (useFeedbackStore().isOpen || feedbackService.promptShownThisSession) return

  if (!Capacitor.isNativePlatform()) return
  if (evaluating || showPrePrompt.value) return
  evaluating = true
  try {
    if (await hasNotificationPermission()) {
      await registerCurrentToken()
      return
    }
    const { value: prompted } = await Preferences.get({ key: OS_PROMPTED_KEY })
    if (prompted === 'true') return // OS prompt already consumed; respect it.

    if (await shouldShowNow()) {
      await recordShown()
      feedbackService.markSystemPromptShown()
      showPrePrompt.value = true
    }
  } finally {
    evaluating = false
  }
}

/** User tapped "Enable" in the pre-prompt → trigger the OS prompt once. */
async function acceptPrePrompt(): Promise<void> {
  showPrePrompt.value = false
  await Preferences.set({ key: OS_PROMPTED_KEY, value: 'true' })
  const granted = await requestNotificationPermission()
  if (granted) {
    await registerCurrentToken()
  }
}

/**
 * Demande posée depuis Réglages > Notifications. Le parent vient de toucher
 * « Autoriser » : ce geste tient lieu de pré-demande, on ouvre donc la demande
 * système directement. Retourne true si elle est accordée.
 */
async function requestFromSettings(): Promise<boolean> {
  showPrePrompt.value = false
  await Preferences.set({ key: OS_PROMPTED_KEY, value: 'true' })
  const granted = await requestNotificationPermission()
  if (granted) await registerCurrentToken()
  return granted
}

/** User tapped "Later" — keep the pre-prompt re-showable, don't touch the OS. */
function dismissPrePrompt(): void {
  showPrePrompt.value = false
}

export function useNotificationPermission() {
  return {
    showPrePrompt,
    maybeAskAfterValidation,
    acceptPrePrompt,
    requestFromSettings,
    dismissPrePrompt,
  }
}
